// @flow
import React, { Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import { H1, H5 } from '@finect/tabular-core/Headings';
import { Small } from '@finect/tabular-core/Small';
import { Strong } from '@finect/tabular-core/Strong';
import { ArrowRight } from '@finect/tabular-icons/arrowRight';
import { Image } from '@finect/tabular-components/Image';
import { GooglePublisherTag } from '@finect/tabular-components/Ads';
import { SHARE_IMAGE } from '../../data/seo';
import { Ad, NewSectionInner, InsideNavigation, BottomNavigation, BottomCTA } from '../';
import { navigationItems, Article, ArticleLink, OtherArticles, OtherSectionLink } from './partials';

type Props = {
  location: Object,
  title: string,
  metaTitle?: string,
  schemaORG?: Object,
  image?: string,
  redirectTo: Function,
  toggleModal: Function,
  children?: any
};

export class ArticleDetail extends React.Component<Props> {
  filterSelect: HTMLSelectElement;

  /* eslint-disable */
  clickNav = (event: SyntheticEvent<HTMLElement>) => {};

  render() {
    const { title, metaTitle, children, location, redirectTo, toggleModal, schemaORG, image } = this.props;
    const currentPath = location.pathname.replace(/\/\s*$/, '');
    const current = navigationItems.find((item) => currentPath.includes(item.path));
    const currentArticle = current.sublinks.find((link) => link.path === currentPath);

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Inicio',
          item: 'https://www.finect.com'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Asesores financieros',
          item: 'https://www.finect.com/asesores'
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: title,
          item: `https://www.finect.com${(currentArticle && currentArticle.path) || currentPath}`
        }
      ]
    };

    const baseUrl = `https://${process.env.FINECT_BASEURL || 'www.finect.com'}`;
    const articleCanonical = `${baseUrl}${(currentArticle && currentArticle.path) || ''}`;
    const articleTitle = `${metaTitle || title} - Finect Asesores`;
    const articleDescription = (currentArticle && currentArticle.text) || '';
    const shareImage = image ? `${baseUrl}/image/${image}` : SHARE_IMAGE;
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: metaTitle || title,
      description: articleDescription,
      image: shareImage,
      inLanguage: 'es-ES',
      mainEntityOfPage: { '@type': 'WebPage', '@id': articleCanonical },
      author: { '@type': 'Organization', name: 'Finect', url: baseUrl },
      publisher: {
        '@type': 'Organization',
        name: 'Finect',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.finect.com/img/logo/logo-white-blue-400x400.jpg'
        }
      }
    };

    return (
      <Fragment>
        <Helmet>
          <title>{articleTitle}</title>
          <meta name="description" content={articleDescription} />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={articleCanonical} />

          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
          {schemaORG && <script type="application/ld+json">{JSON.stringify(schemaORG)}</script>}
          <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

          <meta property="og:site_name" content="Finect" />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={articleTitle} />
          <meta property="og:description" content={articleDescription} />
          <meta property="og:url" content={articleCanonical} />
          <meta property="og:locale" content="es_ES" />
          <meta property="og:image" content={shareImage} />
          <meta property="og:image:width" content="1200" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@finect" />
          <meta property="twitter:title" content={articleTitle} />
          <meta property="twitter:description" content={articleDescription} />
          <meta property="twitter:image" content={shareImage} />
        </Helmet>
        <InsideNavigation
          links={navigationItems}
          currentPath={currentPath}
          redirectTo={redirectTo}
          toggleModal={toggleModal}
        />
        <NewSectionInner withInsideNavigation>
          <Article>
            <H1 large>{title}</H1>

            {image && <Image src={`/image/${image}`} width={1200} format="webp" alt={title} />}

            {children}
          </Article>
        </NewSectionInner>
        <BottomCTA toggleModal={toggleModal} />
        <NewSectionInner>
          <OtherArticles>
            <div>
              <H5>{current.othertitle}</H5>
              <ul>
                {current.sublinks.map((article, index) => {
                  if (article.path === currentPath) return null;
                  return (
                    <li key={index}>
                      <ArticleLink visible="true" onClick={this.clickNav} to={article.path}>
                        <span>
                          {article.title}
                          <ArrowRight size="18" />
                        </span>
                      </ArticleLink>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <H5>También te puede interesar:</H5>
              {navigationItems.map((item, index) => {
                if (currentPath.includes(item.path)) return null;
                return (
                  <OtherSectionLink onClick={this.clickNav} key={index} to={item.path}>
                    <Strong>{item.title}</Strong>
                    <Small compact>{item.subtitle}</Small>
                    <ArrowRight size="18" />
                  </OtherSectionLink>
                );
              })}
            </div>
          </OtherArticles>
          <Ad padding="30px 0 42px">
            <Small light>Patrocinio</Small>
            <GooglePublisherTag
              path="/7711056/Finect_Patrocinio_Asesores"
              dimensions={[
                [970, 90],
                [728, 90],
                [300, 150],
              ]}
              enableSingleRequest={true}
              collapseEmptyDiv={true}
              canBeLower={false}
            />
          </Ad>
        </NewSectionInner>
        <BottomNavigation
          links={[
            { title: 'Asesores', path: '/asesores' },
            { title: current.title, path: current.path },
            { title: currentArticle && currentArticle.title },
          ]}
        />
      </Fragment>
    );
  }
}
